import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Select } from '@mantine/core';
import { Button as SharedButton } from '../../../../components/Button';
import { PrinterView, printersService } from '../../../../services/printers/printers.service';
import { extractServiceErrorMessage } from '../../../../services/shared/service-error.util';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './shared/settings-shared.module.scss';

type PrinterRoutingSectionProps = {
  tenantId: string;
  employeeSession: string;
  printers: PrinterView[];
};

const CLEAR_ROUTE_VALUE = 'none';

type PendingRoute = { origin: PrinterView; targetId: number | null; targetName: string | null };

// Summary: Manager POS-side printer routing override — redirect printer X's traffic to Y.
// Selecting a target opens a confirm guard (Rule 39); the PATCH sends only routeToPrinterId and the
// printer.upserted SSE delta patches the row, so no manual refetch is needed.
export const PrinterRoutingSection: React.FC<PrinterRoutingSectionProps> = ({
  tenantId,
  employeeSession,
  printers,
}) => {
  const [pending, setPending] = useState<PendingRoute | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmInFlightRef = useRef(false);

  // Defensive: a broken upstream SSE merge may hand us undefined/holes; never crash.
  const activePrinters = useMemo(
    () => (Array.isArray(printers) ? printers.filter((printer) => printer && printer.active) : []),
    [printers],
  );

  const handleSelect = useCallback(
    (origin: PrinterView, value: string | null) => {
      const targetId = value === null || value === CLEAR_ROUTE_VALUE ? null : Number(value);
      if (targetId === (origin.routeToPrinterId ?? null)) {
        return;
      }
      const targetName = targetId === null ? null : activePrinters.find((p) => p.id === targetId)?.name ?? null;
      setError(null);
      setPending({ origin, targetId, targetName });
    },
    [activePrinters],
  );

  const handleConfirm = useCallback(async () => {
    if (!pending || confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await printersService.updatePrinterRoute(tenantId, pending.origin.id, pending.targetId, employeeSession);
      setPending(null);
    } catch (err: unknown) {
      setError(extractServiceErrorMessage(err, 'עדכון הניתוב נכשל'));
    } finally {
      confirmInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [pending, tenantId, employeeSession]);

  return (
    <section data-testid="printer-routing-section" className={classes.section}>
      <h3 className={classes.sectionHeading}>ניתוב מדפסות</h3>
      <p className={classes.lead}>
        נתב מדפסת שהתקלקלה למדפסת אחרת — כל בון או קבלה שהיו מודפסים במדפסת המקור יודפסו ביעד עד לביטול הניתוב.
      </p>

      {error && <div className={classes.error}>{error}</div>}

      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>מדפסת</th>
              <th>נתב אל</th>
              <th>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {activePrinters.map((printer) => {
              const targetOptions = activePrinters
                .filter((candidate) => candidate.id !== printer.id)
                .map((candidate) => ({ value: String(candidate.id), label: candidate.name }));
              const routedId = printer.routeToPrinterId ?? null;
              const routedActive = routedId != null && targetOptions.some((o) => o.value === String(routedId));
              // A disabled routed target is gone from options; keep it shown (disabled).
              const routedGoneOption =
                routedId != null && !routedActive
                  ? [{ value: String(routedId), label: `${printer.routeToPrinterName ?? 'מדפסת'} (מושבתת)`, disabled: true }]
                  : [];
              return (
                <tr key={printer.id} data-testid={`printer-routing-row-${printer.id}`}>
                  <td>{printer.name}</td>
                  <td className={classes.parentCell}>
                    <Select
                      data-testid={`printer-routing-select-${printer.id}`}
                      aria-label={`ניתוב ${printer.name}`}
                      data={[{ value: CLEAR_ROUTE_VALUE, label: 'ללא ניתוב' }, ...targetOptions, ...routedGoneOption]}
                      value={routedId != null ? String(routedId) : CLEAR_ROUTE_VALUE}
                      onChange={(value) => handleSelect(printer, value)}
                      disabled={isSaving || (targetOptions.length === 0 && routedId == null)}
                      radius="md"
                    />
                  </td>
                  <td>
                    {routedId != null ? (
                      <span className={classes.statusInactive} data-testid={`printer-routing-hint-${printer.id}`}>
                        מנותב אל: {printer.routeToPrinterName ?? '—'}{routedActive ? '' : ' (מושבתת)'}
                      </span>
                    ) : (
                      <span className={classes.statusActive}>ללא ניתוב</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {activePrinters.length === 0 && (
              <tr>
                <td colSpan={3} className={classes.empty}>אין מדפסות פעילות לניתוב.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        opened={pending !== null}
        onClose={() => (isSaving ? undefined : setPending(null))}
        title="אישור ניתוב מדפסת"
        closeButtonProps={{ 'data-testid': 'printer-routing-confirm-close' } as Record<string, string>}
        centered
        radius="md"
        classNames={{ root: navyModal.root }}
      >
        {pending && (
          <div className={classes.section} data-testid="printer-routing-confirm">
            <p className={classes.lead}>
              {pending.targetId === null
                ? `לבטל את הניתוב של «${pending.origin.name}»?`
                : `לנתב את «${pending.origin.name}» אל «${pending.targetName ?? 'המדפסת שנבחרה'}»? כל התעבורה תעבור ליעד.`}
            </p>
            <div className={classes.formActions}>
              <SharedButton
                variant="cancel"
                size="sm"
                dataTestId="printer-routing-cancel"
                onClick={() => setPending(null)}
                disabled={isSaving}
              >
                ביטול
              </SharedButton>
              <SharedButton
                variant="save"
                size="sm"
                dataTestId="printer-routing-apply"
                onClick={() => void handleConfirm()}
                disabled={isSaving}
              >
                {isSaving ? 'שומר...' : 'אישור'}
              </SharedButton>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};
